import { AutoTranslate, LocalizedCaseManagerField, LocalizedChannelDetails, LocalizedDepartment, LocalizedFAQItems, LocalizedProgramTabs, LocalizedReportCustomField, LocalizedReportCustomFieldConditionalLogic, LocalizedReportType, LocalizedTenant, UpdateCaseManagerFieldTranslation, UpdateDepartmentTranslation, UpdateFAQTranslation, UpdatePolicyTranslation, UpdateReportCustomFieldConditionalLogicTranslation, UpdateReportCustomFieldTranslation, UpdateReportTypeTranslation, UpdateTenantTranslation } from './type';

import { restAPI } from '../rest-api';

import { AtLeastOne } from '~/utils/type-helpers';


const endpoints = restAPI.injectEndpoints({
  endpoints: (builder) => ({
    otherChannelData: builder.query<LocalizedChannelDetails[], {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/form-type-tenant-management`,
        };
      },
      providesTags: (res, _err, args) =>
        res ? [{ type: 'Localization', id: 'localized-channel-details' }, { type: 'Localization', id: `localized-channel-${args.selectedChannel}-details` }] : [],
    }),
    // PROGRAM POLICY
    programTabs: builder.query<LocalizedProgramTabs, {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/program-settings-tab`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Localization', id: 'localized-policy' }] : [],
    }),
    // FAQ
    faqs: builder.query<LocalizedFAQItems, {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/program-settings-tab-faq`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Localization', id: 'localized-faq' }] : [],
    }),
    // DEPARTMENT
    departments: builder.query<LocalizedDepartment, {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/department`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Localization', id: 'localized-department' }] : [],
    }),
    // REPORT TYPE
    reportType: builder.query<LocalizedReportType, {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/report-type`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Localization', id: 'localized-report-type' }] : [],
    }),
    // CUSTOM FIELDS
    reportCustomFields: builder.query<LocalizedReportCustomField, {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/report-custom-field`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Localization', id: 'localized-report-custom-field' }] : [],
    }),
    reportCustomFieldConditionalLogics: builder.query<LocalizedReportCustomFieldConditionalLogic, {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/report-custom-field-conditional-logic`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Localization', id: 'localized-report-custom-field-conditional-logic' }] : [],
    }),
    caseManagerFields: builder.query<LocalizedCaseManagerField, {
      slug: string;
      selectedChannel: number;
    }>({
      query: ({ slug, selectedChannel }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/case-manager-field`,
        };
      },
      providesTags: (res) =>
        res ? [{ type: 'Localization', id: 'localized-case-manager-field' }] : [],
    }),

    // AUTO TRANSLATE TENANT DATA
    autoTranslate: builder.mutation<void, {
      slug: string;
      selectedChannel: number;
      payload: AutoTranslate
    }>({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/auto-translate`,
          method: 'POST',
          data: payload,
        };
      },
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      invalidatesTags: (res, _err, _args) => res ? [
        { type: 'Localization', id: 'localized-tenant' },
        { type: 'Localization', id: 'localized-report-custom-field' },
        { type: 'Localization', id: 'localized-report-type' },
        { type: 'Localization', id: 'localized-department' },
        { type: 'Localization', id: 'localized-policy' },
        { type: 'Localization', id: 'localized-channel-details' },
        { type: 'Localization', id: 'localized-case-manager-field' },
        { type: 'Localization', id: 'localized-report-custom-field-conditional-logic' },
        { type: 'Localization', id: 'localized-faq' },
      ] : [],
    }),
    // UPDATE TENANT TRANSLATION
    updateTenantTranslation: builder.mutation<void,
      {
        slug: string;
        selectedChannel: number;
        payload: AtLeastOne<Omit<UpdateTenantTranslation, 'language_id' | 'main_id'>> & Pick<UpdateTenantTranslation, 'language_id' | 'main_id'>
      }
    >({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/form-type-tenant-management`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res, _err, args) => res ? [
        { type: 'Localization', id: 'localized-tenant' },
        { type: 'Localization', id: 'localized-report-custom-field' },
        { type: 'Localization', id: 'localized-report-type' },
        { type: 'Localization', id: 'localized-channel-details' },
        { type: 'Localization', id: `localized-channel-${args.selectedChannel}-details` },
        { type: 'Localization', id: 'localized-policy' },
        { type: 'Localization', id: 'localized-case-manager-field' },
        { type: 'Localization', id: 'localized-report-custom-field-conditional-logic' },
      ] : [],
    }),
    // UPDATE POLICY TRANSLATION
    updateCustomTabTranslation: builder.mutation<void,
      {
        slug: string;
        selectedChannel: number;
        payload: UpdatePolicyTranslation
      }
    >({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/program-settings-tab`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res) => res ? [
        { type: 'Localization', id: 'localized-tenant' },
        { type: 'Localization', id: 'localized-policy' },
      ] : [],
    }),
    // UPDATE FAQ TRANSLATION
    updateFAQTranslation: builder.mutation<void,
      {
        slug: string;
        selectedChannel: number;
        payload: UpdateFAQTranslation[],
      }
    >({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/program-settings-tab-faq`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res) => res ? [{ type: 'Localization', id: 'localized-faq' }] : [],
    }),
    // UPDATE DEPARTMENT TRANSLATION
    updateDepartmentTranslation: builder.mutation<void,
      {
        slug: string;
        selectedChannel: number;
        payload: UpdateDepartmentTranslation,
      }
    >({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/department`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res) => res ? [{ type: 'Localization', id: 'localized-department' }] : [],
    }),
    // UPDATE REPORT TYPE TRANSLATION
    updateReportTypeTranslation: builder.mutation<void, {
      slug: string;
      selectedChannel: number;
      payload: UpdateReportTypeTranslation
    }>({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/report-type`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res) => res ? [{ type: 'Localization', id: 'localized-report-type' }] : [],
    }),
    // UPDATE CUSTOM FIELD TRANSLATION
    updateReportCustomFieldTranslation: builder.mutation<void, {
      slug: string;
      selectedChannel: number;
      payload: UpdateReportCustomFieldTranslation
    }>({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/report-custom-field`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res) => res ? [{ type: 'Localization', id: 'localized-report-custom-field' }] : [],
    }),
    getLocalizedTenantData: builder.query<LocalizedTenant, void>({
      query: () => {
        return {
          url: '/localization/tenant',
        };
      },
      providesTags: (res) => res ? [{ type: 'Localization', id: 'localized-tenant' }] : [],
    }),
    updateLocalizedTenantData: builder.mutation<void,
      {
        payload: {
          bio: string,
          main_id: number,
          language_id: number,
        }
      }>({
        query: ({ payload }) => {
          return {
            url: '/localization/tenant',
            method: 'POST',
            data: payload,
          };
        },
        invalidatesTags: (res) => res ? [{ type: 'Localization', id: 'localized-tenant' }] : [],
      }),
    // UPDATE CUSTOM FIELD'S CONDITIONAL LOGIC TRANSLATION
    updateReportCustomFieldConditionalLogicTranslation: builder.mutation<void, {
      slug: string;
      selectedChannel: number;
      payload: UpdateReportCustomFieldConditionalLogicTranslation,
    }>({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/report-custom-field-conditional-logic`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res) => res ? [{ type: 'Localization', id: 'localized-report-custom-field-conditional-logic' }] : [],
    }),
    // UPDATE CASE ACTION FIELD TRANSLATION
    updateCaseManagerFieldTranslation: builder.mutation<void, {
      slug: string;
      selectedChannel: number;
      payload: UpdateCaseManagerFieldTranslation,
    }>({
      query: ({ slug, selectedChannel, payload }) => {
        return {
          url: `/intake-channel/tenant/${slug}/${selectedChannel}/localization/case-manager-field`,
          method: 'POST',
          data: payload,
        };
      },
      invalidatesTags: (res) => res ? [{ type: 'Localization', id: 'localized-case-manager-field' }] : [],
    }),
  }),
  overrideExisting: true,
});

export const LocalizationService = endpoints;
